import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Users, Plus, Trash2, Upload, X, Loader2, ArrowLeft, UserPlus, Pencil, Search, List, ChevronDown, FileSpreadsheet, CheckCircle2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { ZONAS_MALAGA, normalizarZonaMalaga } from '../utils/zonasMalaga';

const API_BASE = 'https://warngen-api-352514293915.europe-west1.run.app';

// --- MODAL GENÉRICO ---
function Modal({ title, onClose, children }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white">{title}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X size={24} /></button>
                </div>
                <div className="p-6 max-h-[85vh] overflow-y-auto">{children}</div>
            </div>
        </div>
    );
}

// --- FORMULARIO CONTACTO ---
function ContactoForm({ initial = {}, onSave, onCancel, saving }) {
    const [form, setForm] = useState({
        nombre: initial.nombre || '',
        email: initial.email || '',
        telefono: initial.telefono || '',
        tipo_usuario: initial.tipo_usuario || '',
        empresa: initial.empresa || '',
        zona: normalizarZonaMalaga(initial.zona || initial.distrito_municipal || ''),
    });
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(form);
    };

    const inputClass = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none";
    const labelClass = "block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide";

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                    <label className={labelClass}>Nombre *</label>
                    <input required type="text" value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Juan García" className={inputClass} />
                </div>
                <div>
                    <label className={labelClass}>Email</label>
                    <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="juan@ejemplo.com" className={inputClass} />
                </div>
                <div>
                    <label className={labelClass}>Teléfono</label>
                    <input type="tel" value={form.telefono} onChange={e => set('telefono', e.target.value)} placeholder="+34 600 000 000" className={inputClass} />
                </div>
                <div className="sm:col-span-2">
                    <label className={labelClass}>Zona (Distrito / Barriada de Málaga)</label>
                    <select value={form.zona} onChange={e => set('zona', e.target.value)} className={inputClass}>
                        <option value="">-- Seleccionar zona --</option>
                        {form.zona && !ZONAS_MALAGA.includes(form.zona) && (
                            <option value={form.zona}>{form.zona}</option>
                        )}
                        {ZONAS_MALAGA.map(z => (
                            <option key={z} value={z}>{z}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className={labelClass}>Tipo / Perfil</label>
                    <input type="text" value={form.tipo_usuario} onChange={e => set('tipo_usuario', e.target.value)} placeholder="Vecino, Comercio..." className={inputClass} />
                </div>
                <div className="sm:col-span-2">
                    <label className={labelClass}>Empresa</label>
                    <input type="text" value={form.empresa} onChange={e => set('empresa', e.target.value)} placeholder="Empresa S.L." className={inputClass} />
                </div>
            </div>
            <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 rounded-xl text-sm font-medium">Cancelar</button>
                <button type="submit" disabled={saving} className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
                    {saving && <Loader2 size={16} className="animate-spin" />}
                    {saving ? 'Guardando...' : 'Guardar'}
                </button>
            </div>
        </form>
    );
}


export default function ListasManager({ darkMode }) {
    const [listas, setListas] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(null);

    // Vista detalle
    const [listaActiva, setListaActiva] = useState(null);
    const [contactosLista, setContactosLista] = useState([]);
    const [cargandoContactos, setCargandoContactos] = useState(false);
    const [busqueda, setBusqueda] = useState('');

    // Modales: 'crear-lista' | 'importar-archivo' | 'add-contacto' | 'edit-contacto' | 'upload-csv' | 'importar-lista'
    const [modal, setModal] = useState(null);
    const [contactoEditando, setContactoEditando] = useState(null);
    const [saving, setSaving] = useState(false);

    // Crear lista manual
    const [nuevaListaNombre, setNuevaListaNombre] = useState('');
    const [nuevaListaDesc, setNuevaListaDesc] = useState('');

    // Importar archivo nuevo (Excel o CSV)
    const [archivoImport, setArchivoImport] = useState(null);
    const [nombreImportLista, setNombreImportLista] = useState('');
    const [descImportLista, setDescImportLista] = useState('');
    const [previewInfo, setPreviewInfo] = useState(null);
    const [importando, setImportando] = useState(false);

    // Upload a lista existente
    const [archivoUpload, setArchivoUpload] = useState(null);
    const [subiendoCSV, setSubiendoCSV] = useState(false);
    const fileInputRef = useRef(null);

    // Importar de otra lista
    const [listaImportarId, setListaImportarId] = useState('');

    const getHeaders = () => {
        const token = localStorage.getItem('token');
        return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    };

    const cargarListas = useCallback(async () => {
        setCargando(true);
        try {
            const res = await fetch(`${API_BASE}/api/listas`, { headers: getHeaders() });
            const data = await res.json();
            if (data.success) setListas(data.data);
            else setError(data.error);
        } catch { setError('Error de conexión al cargar listas.'); }
        finally { setCargando(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { cargarListas(); }, [cargarListas]);

    const abrirLista = async (lista) => {
        setListaActiva(lista);
        setBusqueda('');
        setCargandoContactos(true);
        try {
            const res = await fetch(`${API_BASE}/api/listas/${lista.id}/contactos`, { headers: getHeaders() });
            const data = await res.json();
            if (data.success) setContactosLista(data.data);
        } catch { console.error('Error cargando contactos'); }
        finally { setCargandoContactos(false); }
    };

    const cerrarLista = () => { setListaActiva(null); setContactosLista([]); };
    const cerrarModal = () => {
        setModal(null);
        setContactoEditando(null);
        setArchivoUpload(null);
        setArchivoImport(null);
        setNombreImportLista('');
        setDescImportLista('');
        setPreviewInfo(null);
        setListaImportarId('');
    };

    // --- Crear lista manual vacía ---
    const handleCrearLista = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await fetch(`${API_BASE}/api/listas`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ nombre: nuevaListaNombre, descripcion: nuevaListaDesc })
            });
            const data = await res.json();
            if (data.success) { cerrarModal(); setNuevaListaNombre(''); setNuevaListaDesc(''); cargarListas(); }
            else alert(data.error);
        } catch { alert('Error al crear la lista.'); }
        finally { setSaving(false); }
    };

    // --- Borrar lista ---
    const confirmarBorrarLista = async (id, nombre) => {
        if (!window.confirm(`¿Seguro que deseas eliminar la lista "${nombre}"? Los contactos no se borrarán del sistema.`)) return;
        try {
            const res = await fetch(`${API_BASE}/api/listas/${id}`, { method: 'DELETE', headers: getHeaders() });
            const data = await res.json();
            if (data.success) cargarListas();
            else alert(data.error);
        } catch { alert('Error de conexión al eliminar la lista.'); }
    };

    // --- Añadir contacto a lista ---
    const handleAñadirContacto = async (contacto) => {
        setSaving(true);
        try {
            const res = await fetch(`${API_BASE}/api/listas/${listaActiva.id}/contactos`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ nuevoContacto: contacto })
            });
            const data = await res.json();
            if (data.success) {
                abrirLista(listaActiva);
                cargarListas();
                cerrarModal();
            } else alert(data.error);
        } catch { alert('Error al guardar el contacto.'); }
        finally { setSaving(false); }
    };

    // --- Editar contacto ---
    const handleEditarContacto = async (contacto) => {
        setSaving(true);
        try {
            const res = await fetch(`${API_BASE}/api/listas/${listaActiva.id}/contactos/${contactoEditando.id}`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify(contacto)
            });
            const data = await res.json();
            if (data.success) {
                abrirLista(listaActiva);
                cerrarModal();
            } else alert(data.error);
        } catch { alert('Error al guardar cambios.'); }
        finally { setSaving(false); }
    };

    // --- Quitar contacto de lista ---
    const quitarContacto = async (contactoId, nombre) => {
        if (!window.confirm(`¿Quitar a "${nombre}" de esta lista?`)) return;
        try {
            const res = await fetch(`${API_BASE}/api/listas/${listaActiva.id}/contactos/${contactoId}`, { method: 'DELETE', headers: getHeaders() });
            const data = await res.json();
            if (data.success) { setContactosLista(prev => prev.filter(c => c.id !== contactoId)); cargarListas(); }
            else alert(data.error);
        } catch { alert('Error de conexión.'); }
    };

    // --- Previsualizar archivo al seleccionarlo ---
    const handleSeleccionarArchivoImport = (file) => {
        if (!file) return;
        setArchivoImport(file);

        // Limpiar nombre por defecto
        const baseName = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ').trim();
        if (!nombreImportLista) {
            setNombreImportLista(baseName.charAt(0).toUpperCase() + baseName.slice(1));
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
                if (rows && rows.length > 0) {
                    const headers = (rows[0] || []).map(h => String(h || '').trim()).filter(Boolean);
                    const rowCount = rows.length - 1;
                    setPreviewInfo({
                        rowCount: Math.max(0, rowCount),
                        headers
                    });
                }
            } catch (err) {
                console.error('Error previsualizando archivo:', err);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    // --- Subir Excel/CSV para crear nueva lista ---
    const handleImportarArchivo = async (e) => {
        e.preventDefault();
        if (!archivoImport) return alert('Por favor selecciona un archivo Excel (.xlsx / .xls) o CSV (.csv)');
        setImportando(true);
        const formData = new FormData();
        formData.append('file', archivoImport);
        formData.append('nombre_lista', nombreImportLista.trim());
        formData.append('descripcion', descImportLista.trim());

        try {
            const res = await fetch(`${API_BASE}/api/listas/importar`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: formData
            });
            const text = await res.text();
            let data;
            try {
                data = JSON.parse(text);
            } catch {
                throw new Error(`El servidor tardó demasiado o devolvió un error (HTTP ${res.status})`);
            }
            if (data.success) {
                alert(`¡Éxito! ${data.message}`);
                cerrarModal();
                cargarListas();
            } else {
                alert('Error: ' + (data.error || 'No se pudo importar la lista'));
            }
        } catch (err) {
            alert('Error al importar el archivo: ' + err.message);
        } finally {
            setImportando(false);
        }
    };

    // --- Subir Excel/CSV a una lista existente ---
    const handleSubirArchivoALista = async (e) => {
        e.preventDefault();
        if (!archivoUpload) return alert('Selecciona un archivo Excel (.xlsx / .xls) o CSV (.csv)');
        setSubiendoCSV(true);
        const formData = new FormData();
        formData.append('file', archivoUpload);
        try {
            const res = await fetch(`${API_BASE}/api/listas/${listaActiva.id}/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: formData
            });
            const text = await res.text();
            let data;
            try {
                data = JSON.parse(text);
            } catch {
                throw new Error(`El servidor tardó demasiado o devolvió un error (HTTP ${res.status})`);
            }
            if (data.success) {
                alert(`¡Éxito! ${data.message}`);
                cerrarModal();
                abrirLista(listaActiva);
                cargarListas();
            } else {
                alert('Error: ' + data.error);
            }
        } catch (err) {
            alert('Error al subir el archivo: ' + (err.message || 'Error de conexión'));
        } finally {
            setSubiendoCSV(false);
        }
    };

    // --- Importar desde otra lista ---
    const handleImportarLista = async (e) => {
        e.preventDefault();
        if (!listaImportarId) return;
        setSaving(true);
        try {
            const res = await fetch(`${API_BASE}/api/listas/${listaActiva.id}/importar-lista/${listaImportarId}`, {
                method: 'POST',
                headers: getHeaders()
            });
            const data = await res.json();
            if (data.success) { alert(data.message); cerrarModal(); abrirLista(listaActiva); cargarListas(); }
            else alert(data.error);
        } catch { alert('Error al importar la lista.'); }
        finally { setSaving(false); }
    };

    const contactosFiltrados = contactosLista.filter(c => {
        const q = busqueda.toLowerCase();
        return !q || c.nombre?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.telefono?.includes(q);
    });

    // ===================== VISTA DETALLE LISTA =====================
    if (listaActiva) {
        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex items-center gap-4">
                        <button onClick={cerrarLista} className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition text-gray-600 dark:text-gray-300">
                            <ArrowLeft size={24} />
                        </button>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                                <Users size={28} className="text-primary-600" />
                                {listaActiva.nombre}
                            </h2>
                            {listaActiva.descripcion && <p className="text-gray-500 dark:text-gray-400 text-sm">{listaActiva.descripcion}</p>}
                        </div>
                    </div>
                    {/* Botones de acción */}
                    <div className="flex flex-wrap gap-2">
                        <button onClick={() => setModal('add-contacto')}
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-xl flex items-center gap-2 text-sm font-medium transition shadow-sm">
                            <UserPlus size={16} /> Nuevo contacto
                        </button>
                        <button onClick={() => setModal('importar-lista')}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-xl flex items-center gap-2 text-sm font-medium transition shadow-sm">
                            <List size={16} /> Importar lista
                        </button>
                        <button onClick={() => setModal('upload-csv')}
                            className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-2 rounded-xl flex items-center gap-2 text-sm font-medium transition shadow-sm">
                            <Upload size={16} /> Subir Excel / CSV
                        </button>
                    </div>
                </div>

                {/* Tabla de contactos */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                    <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
                        <h3 className="font-semibold text-gray-800 dark:text-white">Contactos ({contactosLista.length})</h3>
                        <div className="relative w-full sm:w-72">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar en esta lista..."
                                value={busqueda}
                                onChange={e => setBusqueda(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 outline-none text-gray-800 dark:text-white"
                            />
                        </div>
                    </div>

                    {cargandoContactos ? (
                        <div className="p-8 text-center text-primary-600"><Loader2 className="animate-spin mx-auto" size={24} /></div>
                    ) : contactosFiltrados.length === 0 ? (
                        <div className="p-12 text-center text-gray-400">
                            {busqueda ? 'No hay contactos que coincidan con la búsqueda.' : 'Esta lista aún no tiene contactos. Puedes añadir uno nuevo o subir un archivo Excel/CSV.'}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm text-gray-600 dark:text-gray-300">
                                <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                    <tr>
                                        <th className="p-4">Nombre / Empresa</th>
                                        <th className="p-4">Email</th>
                                        <th className="p-4">Teléfono</th>
                                        <th className="p-4">Zona</th>
                                        <th className="p-4 text-right">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {contactosFiltrados.map(c => (
                                        <tr key={c.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition">
                                            <td className="p-4 font-medium text-gray-900 dark:text-white">
                                                {c.nombre}
                                                {c.empresa && <span className="block text-xs font-normal text-gray-400">{c.empresa}</span>}
                                            </td>
                                            <td className="p-4">{c.email || '—'}</td>
                                            <td className="p-4">{c.telefono || '—'}</td>
                                            <td className="p-4">{c.zona || '—'}</td>
                                            <td className="p-4 text-right">
                                                <div className="flex justify-end gap-1">
                                                    <button
                                                        onClick={() => { setContactoEditando(c); setModal('edit-contacto'); }}
                                                        className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition"
                                                        title="Editar contacto"
                                                    >
                                                        <Pencil size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => quitarContacto(c.id, c.nombre)}
                                                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
                                                        title="Quitar de la lista"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* MODAL: Añadir contacto nuevo */}
                {modal === 'add-contacto' && (
                    <Modal title={`Añadir contacto a "${listaActiva.nombre}"`} onClose={cerrarModal}>
                        <ContactoForm onSave={handleAñadirContacto} onCancel={cerrarModal} saving={saving} />
                    </Modal>
                )}

                {/* MODAL: Editar contacto */}
                {modal === 'edit-contacto' && contactoEditando && (
                    <Modal title={`Editar: ${contactoEditando.nombre}`} onClose={cerrarModal}>
                        <ContactoForm initial={contactoEditando} onSave={handleEditarContacto} onCancel={cerrarModal} saving={saving} />
                    </Modal>
                )}

                {/* MODAL: Subir Excel/CSV a lista activa */}
                {modal === 'upload-csv' && (
                    <Modal title={`Subir Excel o CSV a "${listaActiva.nombre}"`} onClose={cerrarModal}>
                        <form onSubmit={handleSubirArchivoALista} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Archivo (Excel .xlsx / .xls o CSV .csv)</label>
                                <input
                                    type="file" accept=".xlsx,.xls,.csv" ref={fileInputRef}
                                    onChange={e => setArchivoUpload(e.target.files[0])}
                                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 dark:file:bg-gray-700 dark:file:text-gray-300 border border-dashed border-gray-300 dark:border-gray-600 p-3 rounded-xl"
                                />
                                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                    Detección inteligente de columnas: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">nombre / empresa, telefono / móvil, email, localidad</code>. Los duplicados se vincularán automáticamente sin duplicar registros.
                                </p>
                            </div>
                            <div className="flex gap-3 justify-end">
                                <button type="button" onClick={cerrarModal} className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded-xl text-sm">Cancelar</button>
                                <button type="submit" disabled={subiendoCSV || !archivoUpload} className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl flex items-center gap-2 text-sm font-semibold disabled:opacity-60">
                                    {subiendoCSV ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                                    {subiendoCSV ? 'Procesando archivo...' : 'Subir e Importar'}
                                </button>
                            </div>
                        </form>
                    </Modal>
                )}

                {/* MODAL: Importar desde otra lista */}
                {modal === 'importar-lista' && (
                    <Modal title="Importar contactos de otra lista" onClose={cerrarModal}>
                        <form onSubmit={handleImportarLista} className="space-y-4">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Selecciona una lista para copiar todos sus contactos a <strong className="text-gray-800 dark:text-white">"{listaActiva.nombre}"</strong>. Los duplicados se ignorarán.
                            </p>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Lista de origen</label>
                                <div className="relative">
                                    <select
                                        value={listaImportarId}
                                        onChange={e => setListaImportarId(e.target.value)}
                                        required
                                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl text-sm focus:ring-2 focus:ring-primary-500 outline-none appearance-none"
                                    >
                                        <option value="">-- Selecciona una lista --</option>
                                        {listas.filter(l => l.id !== listaActiva.id).map(l => (
                                            <option key={l.id} value={l.id}>{l.nombre} ({l.total_contactos} contactos)</option>
                                        ))}
                                    </select>
                                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                </div>
                            </div>
                            <div className="flex gap-3 justify-end">
                                <button type="button" onClick={cerrarModal} className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded-xl text-sm">Cancelar</button>
                                <button type="submit" disabled={saving || !listaImportarId} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl flex items-center gap-2 text-sm font-semibold disabled:opacity-60">
                                    {saving ? <Loader2 size={16} className="animate-spin" /> : <List size={16} />}
                                    {saving ? 'Importando...' : 'Importar contactos'}
                                </button>
                            </div>
                        </form>
                    </Modal>
                )}
            </div>
        );
    }

    // ===================== VISTA GENERAL DE LISTAS =====================
    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Mis Listas de Contactos</h2>
                    <p className="text-gray-500 dark:text-gray-400">Crea agrupaciones personalizadas para tus envíos por Email o SMS</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button onClick={() => setModal('importar-archivo')}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-xl flex items-center gap-2 shadow-sm transition font-medium text-sm">
                        <FileSpreadsheet size={18} /> Importar Excel o CSV
                    </button>
                    <button onClick={() => setModal('crear-lista')}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2.5 rounded-xl flex items-center gap-2 shadow-sm transition font-medium text-sm">
                        <Plus size={18} /> Crear Lista Vacía
                    </button>
                </div>
            </div>

            {error && <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-xl border border-red-200 dark:border-red-800">{error}</div>}

            {cargando ? (
                <div className="flex justify-center p-12 text-primary-600"><Loader2 className="animate-spin" size={32} /></div>
            ) : listas.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center shadow-sm border border-gray-100 dark:border-gray-700">
                    <FileSpreadsheet size={48} className="mx-auto mb-4 text-emerald-500" />
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">No tienes listas creadas</h3>
                    <p className="text-gray-500 mb-6 max-w-md mx-auto">Sube directamente tu archivo Excel o CSV para crear tu primera lista en 1 solo paso.</p>
                    <div className="flex flex-col sm:flex-row justify-center gap-3">
                        <button onClick={() => setModal('importar-archivo')} className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-xl inline-flex items-center justify-center gap-2 font-medium">
                            <Upload size={18} /> Subir archivo Excel o CSV
                        </button>
                        <button onClick={() => setModal('crear-lista')} className="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-5 py-2.5 rounded-xl inline-flex items-center justify-center gap-2 font-medium">
                            <Plus size={18} /> Lista vacía
                        </button>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {listas.map(lista => (
                        <div key={lista.id} className="bg-white dark:bg-gray-800 rounded-2xl p-5 sm:p-6 shadow-sm hover:shadow-md transition border border-gray-100 dark:border-gray-700 group flex flex-col">
                            <div className="flex justify-between items-start mb-4">
                                <div className="p-3 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-xl">
                                    <Users size={24} />
                                </div>
                                <button
                                    onClick={() => confirmarBorrarLista(lista.id, lista.nombre)}
                                    className="text-gray-400 hover:text-red-500 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                                    title="Eliminar lista"
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-1 line-clamp-1">{lista.nombre}</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-4 flex-1">{lista.descripcion || 'Sin descripción'}</p>
                            <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100 dark:border-gray-700">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300">
                                    <UserPlus size={14} />
                                    {lista.total_contactos} contactos
                                </span>
                                <button onClick={() => abrirLista(lista)} className="text-primary-600 hover:text-primary-700 text-sm font-semibold flex items-center gap-1">
                                    Gestionar <ArrowLeft className="rotate-180" size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* MODAL: Importar Lista desde archivo Excel o CSV (1 Paso) */}
            {modal === 'importar-archivo' && (
                <Modal title="Importar Lista (Excel o CSV)" onClose={cerrarModal}>
                    <form onSubmit={handleImportarArchivo} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Archivo (.xlsx, .xls o .csv) *
                            </label>
                            <input
                                type="file"
                                required
                                accept=".xlsx,.xls,.csv"
                                onChange={e => handleSeleccionarArchivoImport(e.target.files[0])}
                                className="w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 dark:file:bg-gray-700 dark:file:text-gray-300 border-2 border-dashed border-gray-300 dark:border-gray-600 p-4 rounded-xl cursor-pointer"
                            />
                        </div>

                        {previewInfo && (
                            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-3 text-xs text-emerald-800 dark:text-emerald-200 space-y-1">
                                <div className="flex items-center gap-1.5 font-semibold text-sm text-emerald-700 dark:text-emerald-300">
                                    <CheckCircle2 size={16} />
                                    {previewInfo.rowCount} registros detectados
                                </div>
                                <div>
                                    <span className="font-semibold">Columnas encontradas:</span> {previewInfo.headers.join(', ')}
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre de la nueva lista *</label>
                            <input
                                type="text"
                                required
                                value={nombreImportLista}
                                onChange={e => setNombreImportLista(e.target.value)}
                                placeholder="Ej: Empresas Solo Móviles"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descripción (opcional)</label>
                            <input
                                type="text"
                                value={descImportLista}
                                onChange={e => setDescImportLista(e.target.value)}
                                placeholder="Ej: Contactos con teléfono móvil verificado para campaña SMS"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                            />
                        </div>

                        <div className="flex gap-3 justify-end pt-2">
                            <button type="button" onClick={cerrarModal} className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded-xl text-sm font-medium">Cancelar</button>
                            <button
                                type="submit"
                                disabled={importando || !archivoImport}
                                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-semibold disabled:opacity-60 flex items-center gap-2 shadow-sm"
                            >
                                {importando ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                                {importando ? 'Importando lista...' : 'Crear e Importar Lista'}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* MODAL: Crear lista vacía */}
            {modal === 'crear-lista' && (
                <Modal title="Nueva Lista Vacía" onClose={() => { cerrarModal(); setNuevaListaNombre(''); setNuevaListaDesc(''); }}>
                    <form onSubmit={handleCrearLista} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre de la lista *</label>
                            <input
                                type="text" required value={nuevaListaNombre}
                                onChange={e => setNuevaListaNombre(e.target.value)}
                                placeholder="Ej: Clientes VIP"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descripción (opcional)</label>
                            <textarea
                                value={nuevaListaDesc} onChange={e => setNuevaListaDesc(e.target.value)} rows="3"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                            />
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button type="button" onClick={() => { cerrarModal(); setNuevaListaNombre(''); setNuevaListaDesc(''); }}
                                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded-xl text-sm">Cancelar</button>
                            <button type="submit" disabled={saving}
                                className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-sm font-semibold disabled:opacity-60 flex items-center gap-2">
                                {saving && <Loader2 size={16} className="animate-spin" />}
                                Crear Lista
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    );
}
